/**
 * File ownership, demoted on purpose from half the old page to one strip.
 *
 * The stance promise, the export-button aphorism and the raw markdown file all
 * used to argue this; research showed even Obsidian — the strongest ownership
 * brand in notes — gives it one section argued as insurance, never the
 * headline. What survives is a single paragraph that keeps the best sentence
 * ("There's no export button…") and turns the Obsidian mention into a payoff:
 * its graph view draws the second-brain web the section above illustrated.
 *
 * "Plain, readable file" replaces every occurrence of "markdown" — the word
 * appeared on none of the thirteen competitor homepages surveyed, including
 * Obsidian's own.
 */

import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { VaultGlyph } from "@/components/icons";
import { MarketingSection } from "@/components/marketing/section";
import { useAppTheme } from "@/theme/paper-theme";
import { iconSize, spacing } from "@/theme/tokens";
import { PANEL_MEASURE } from "@/theme/web-tokens";

const LEAD = "And the files are yours, for good.";

const BODY =
  " Every note is a plain, readable file in a folder on your phone. Open that folder in Obsidian and the whole web above lights up in its graph view. Back it up, or take it to any notes app that reads plain files. There's no export button because there's nothing to export from.";

export function Ownership() {
  const theme = useAppTheme();

  return (
    <MarketingSection>
      <View style={styles.strip}>
        <View
          style={[
            styles.glyphBox,
            {
              backgroundColor: theme.colors.elevation.level1,
              borderColor: theme.colors.outlineVariant,
            },
          ]}
        >
          <VaultGlyph size={iconSize.xl} color={theme.colors.primary} />
        </View>

        <Text
          variant="bodyLarge"
          style={[styles.body, { color: theme.colors.onSurfaceVariant }]}
        >
          <Text
            variant="bodyLarge"
            style={[styles.lead, { color: theme.colors.onSurface }]}
          >
            {LEAD}
          </Text>
          {BODY}
        </Text>
      </View>
    </MarketingSection>
  );
}

const styles = StyleSheet.create({
  strip: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.lg,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    maxWidth: PANEL_MEASURE,
    width: "100%",
  },
  glyphBox: {
    width: 72,
    height: 72,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  body: {
    flex: 1,
    // Below the wrap point the paragraph takes the whole line; the minimum
    // keeps it from being squeezed beside the glyph into a one-word column.
    minWidth: 260,
  },
  lead: {
    fontWeight: "600",
  },
});
